import React, { createContext, useContext, FC, useMemo, useCallback } from 'react';
import { Router, useHistory, useLocation, useRouteMatch } from 'react-router';
import { History } from 'history';
import { getEnvironment } from '@minecraft.environment';
import { isExternalUrl, buildAudiencePrefixedUrl, parseSearchParams } from './utils';
import { RouterContextValue, NavigateOptions, DefaultRouteParams, DefaultSearchParams } from './types';

const RouterReactContext = createContext<RouterContextValue<DefaultRouteParams, DefaultSearchParams> | null>(null);

type RouteProviderProps = {
  /** children components that will have access to the router context */
  children: React.ReactNode;
  /** history instance for the router */
  history: History;
};

export const RouterContextProvider: FC = ({ children }) => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(
    () => ({
      /** fields from window.location */
      href: window.location.href,
      origin: window.location.origin,
      protocol: window.location.protocol,
      host: window.location.host,
      hostname: window.location.hostname,
      port: window.location.port,
      windowPathname: window.location.pathname,
      /** fields from the SPA router location */
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
      searchParams: parseSearchParams(location.search),
      /** from app ENV */
      basePath: getEnvironment().BASE_NAME,
      /** fields from useRouteMatch that are filled in by useRouter at the call-site */
      routeParams: {} as Record<string, string>,
      matchedPath: '',
    }),
    [location]
  );

  const resolveHref = useCallback(
    ({ to, target = '_self' }: NavigateOptions): string => {
      if (!to || typeof to !== 'string') {
        return '';
      }

      // if the "to" value is to a fully qualified URL, return it as is
      if (isExternalUrl(to)) {
        return to;
      }

      // if we have a relative link that is not targeting the current tab
      // we want to resolve it to a fully qualified URL with the audience prefix,
      // since it will be opened in a new tab and won't have access to the router context to resolve it from there
      if (target !== '_self') {
        return buildAudiencePrefixedUrl(to);
      }

      // at this point we know we're resolving a relative path within the current tab
      // there are 4 remaining cases to handle:
      // 1. the "to" value is an absolute path starting with a "/", which replaces the current path completely
      // 2. the "to" value is a query-string-only value starting with "?", which keeps the current path and replaces only the search
      // 3. the "to" value is a hash-only value starting with "#", which keeps the current path and replaces only the hash
      // 4. the "to" value is a relative path that does not start with any of the above, in which case we need to resolve it relative to the current path
      if (to.startsWith('?') || to.startsWith('#')) {
        return `${location?.pathname}${to}`;
      }
      let resolvedTo = to;
      if (!to.startsWith('/')) {
        const currentPathParts = location?.pathname?.split('/');
        currentPathParts.pop();
        resolvedTo = `${currentPathParts.join('/')}/${to}`;
      }
      return resolvedTo;
    },
    [location?.pathname]
  );

  const navigate = useCallback(
    ({ to, target = '_self' }: NavigateOptions) => {
      if (isExternalUrl(to)) {
        const resolvedHref = resolveHref({ to, target });
        window.open(resolvedHref, '_blank', 'noopener noreferrer');
      } else if (target !== '_self') {
        const resolvedHref = resolveHref({ to, target });
        window.open(resolvedHref, target, 'noopener noreferrer');
      } else {
        // For internal same-tab pushes, resolve ?/# prefixes against history.location.pathname
        // (synchronously current) rather than React state (which may be stale in effects).
        const resolvedHref =
          to.startsWith('?') || to.startsWith('#') ? `${history.location.pathname}${to}` : resolveHref({ to, target });
        history.push(resolvedHref);
      }
    },
    [history, resolveHref]
  );

  const replace = useCallback(
    ({ to }: { to: string }) => {
      if (isExternalUrl(to)) {
        window.open(to, '_blank', 'noopener noreferrer');
      } else {
        const resolvedHref =
          to.startsWith('?') || to.startsWith('#')
            ? `${history.location.pathname}${to}`
            : resolveHref({ to, target: '_self' });
        history.replace(resolvedHref);
      }
    },
    [history, resolveHref]
  );

  const contextValue: RouterContextValue = useMemo(
    () => ({ ...state, navigate, replace, resolveHref }),
    [state, navigate, replace, resolveHref]
  );

  return <RouterReactContext.Provider value={contextValue}>{children}</RouterReactContext.Provider>;
};

/**
 * Context Provider that mounts in the App component of each application package and
 * provides a standard API for route state & callbacks to navigate
 */
export const RouteProvider: FC<RouteProviderProps> = ({ history, children }) => {
  // you might be tempted to instantiate the history singleton here
  // but it makes it much more difficult to test the code compared to
  // instantiating it in the App or Test harness for each use-case
  // there are separate helpers for instantiation in the history.ts file
  return (
    <Router history={history}>
      <RouterContextProvider>{children}</RouterContextProvider>
    </Router>
  );
};

/**
 * Hook to access the router context, including current location and navigate / replace callbacks
 * must be used within a RouteProvider
 *
 * @template RouteParams - Type for route parameters (e.g. { companyId: string })
 * @template SearchParams - Type for search/query parameters (e.g. { from?: string })
 *
 * Note that it needs to reference the route params from react-router's useParams() hook in order to get the dynamic params from the current route, so it will cause a re-render whenever the route changes and the params change, which is what we want in most cases since components that use this hook are likely to need to update when the route changes
 *
 * @example
 * const { searchParams, routeParams } = useRouter<
 *   { companyId: string },
 *   { from?: string }
 * >();
 */
export const useRouter = <
  RouteParams extends DefaultRouteParams = DefaultRouteParams,
  SearchParams extends DefaultSearchParams = DefaultSearchParams
>(): RouterContextValue<RouteParams, SearchParams> => {
  const context = useContext<RouterContextValue<RouteParams, SearchParams> | null>(
    RouterReactContext as unknown as React.Context<RouterContextValue<RouteParams, SearchParams> | null>
  );
  const { path, params } = useRouteMatch<RouteParams>();

  if (!context) {
    throw new Error('useRouter must be used within a RouteProvider');
  }
  return useMemo(
    () => ({
      ...context,
      routeParams: params || context.routeParams,
      matchedPath: path || '',
    }),
    [context, params, path]
  ) as RouterContextValue<RouteParams, SearchParams>;
};
